import { supabase } from './supabase';
import { exerciseCatalogService } from './exerciseCatalog';
import type { 
  TrainingProgram, 
  ProgramWorkout, 
  ProgramExercise
} from '../types';

/**
 * Service for managing training program operations
 * Handles CRUD operations, active program management, and program-workout relationships
 * Implements requirements 3.1, 3.2, 3.5, 3.6, 4.1, 4.2, 4.3, 4.4
 */
export class TrainingProgramService {
  /**
   * Check if running in development mode
   */
  private isDevelopmentMode(): boolean {
    return import.meta.env.VITE_DEV_MODE === 'true';
  }

  /**
   * Create a new training program
   * Requirements: 3.1, 3.5
   */
  async createProgram(name: string, setAsActive: boolean = false, userId?: string): Promise<TrainingProgram> {
    if (!name.trim()) {
      throw new Error('Название программы обязательно');
    }

    if (this.isDevelopmentMode()) {
      console.log('Development mode: creating mock training program');
      
      // Create mock program
      const mockProgram: TrainingProgram = {
        id: `mock-program-${Date.now()}`,
        user_id: 'mock-user-123456789',
        name: name.trim(),
        is_active: setAsActive,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        workouts: []
      };

      return mockProgram;
    }

    try {
      // Get user_id - either from parameter or from current user in database
      let effectiveUserId = userId;
      
      if (!effectiveUserId) {
        // Try to get current user from users table (Telegram auth)
        const { data: currentUserData, error: userError } = await supabase
          .from('users')
          .select('id, telegram_id')
          .limit(1)
          .single();
        
        console.log('🔍 Current user check:', { 
          hasUser: !!currentUserData, 
          userId: currentUserData?.id,
          telegramId: currentUserData?.telegram_id,
          hasError: !!userError,
          errorMessage: userError?.message 
        });
        
        if (userError || !currentUserData) {
          const errorMsg = `Ошибка: пользователь не найден. ${userError?.message || ''}`;
          console.error('❌ User not found:', userError);
          alert(errorMsg);
          throw new Error('Необходимо войти в систему для создания программы');
        }
        
        effectiveUserId = currentUserData.id;
      }

      console.log('✅ Creating program for user:', effectiveUserId);

      // If setting as active, first deactivate all other programs
      if (setAsActive) {
        console.log('🔄 Deactivating other programs...');
        await this.deactivateAllPrograms();
      }

      console.log('📝 Inserting program:', { 
        name: name.trim(), 
        is_active: setAsActive,
        user_id: effectiveUserId 
      });

      const { data, error } = await supabase
        .from('training_programs')
        .insert({
          name: name.trim(),
          is_active: setAsActive,
          user_id: effectiveUserId
        })
        .select()
        .single();

      if (error) {
        const errorDetails = {
          code: error.code,
          message: error.message,
          details: error.details,
          hint: error.hint
        };
        
        console.error('❌ Error creating program:', error);
        console.error('📋 Error details:', errorDetails);
        
        // Show detailed error in alert for debugging
        alert(`Ошибка создания программы:\nКод: ${error.code}\nСообщение: ${error.message}\nДетали: ${JSON.stringify(error.details)}\nПодсказка: ${error.hint}`);
        
        // Provide more user-friendly error messages
        if (error.code === '42501') {
          throw new Error('Недостаточно прав для создания программы. Проверьте авторизацию.');
        } else if (error.code === '23505') {
          throw new Error('Программа с таким названием уже существует');
        } else if (error.code === '23502') {
          throw new Error('Ошибка: не заполнено обязательное поле (возможно user_id)');
        } else {
          throw new Error(`Не удалось создать программу: ${error.message}`);
        }
      }

      if (!data) {
        const errorMsg = 'Не удалось получить данные созданной программы';
        console.error('❌', errorMsg);
        alert(errorMsg);
        throw new Error(errorMsg);
      }

      console.log('✅ Program created successfully:', data.id);

      return {
        ...data,
        workouts: []
      };
    } catch (error) {
      console.error('❌ Exception in createProgram:', error);
      const errorMsg = error instanceof Error ? error.message : 'Неизвестная ошибка при создании программы';
      
      // Show error in alert if not already shown
      if (error instanceof Error && !error.message.includes('Ошибка создания программы:')) {
        alert(`Исключение: ${errorMsg}`);
      }
      
      throw error instanceof Error ? error : new Error('Неизвестная ошибка при создании программы');
    }
  }

  /**
   * Get all programs for the current user
   * Requirements: 4.1
   * @param userId - Optional user ID, if not provided will try to get from users table
   */
  async getAllPrograms(userId?: string): Promise<TrainingProgram[]> {
    console.log('🔍 getAllPrograms called with userId:', userId);
    
    let effectiveUserId = userId;
    
    // If no userId provided, try to get from users table
    if (!effectiveUserId) {
      const { data: currentUserData, error: userError } = await supabase
        .from('users')
        .select('id, telegram_id')
        .limit(1)
        .single();
      
      if (userError || !currentUserData) {
        console.error('❌ Error getting current user:', userError);
        throw new Error('Не удалось получить данные пользователя');
      }
      
      effectiveUserId = currentUserData.id;
    }

    console.log('✅ Loading programs for user:', effectiveUserId);

    // Load programs with workouts, but without exercise details
    const { data, error } = await supabase
      .from('training_programs')
      .select(`
        *,
        program_workouts (
          *,
          program_exercises (
            id,
            workout_id,
            exercise_id,
            order_index,
            target_sets,
            target_reps_min,
            target_reps_max,
            rest_seconds
          )
        )
      `)
      .eq('user_id', effectiveUserId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('❌ Error fetching programs:', error);
      console.error('📋 Error details:', {
        code: error.code,
        message: error.message,
        details: error.details
      });
      throw new Error(`Failed to fetch programs: ${error.message}`);
    }

    console.log(`✅ Found ${data?.length || 0} programs`);

    // Transform data and load exercise details separately
    return this.transformProgramsDataWithExercises(data || []);
  }

  /**
   * Get a specific program by ID with full details
   * Requirements: 4.1
   */
  async getProgramById(id: string): Promise<TrainingProgram | null> {
    const { data, error } = await supabase
      .from('training_programs')
      .select(`
        *,
        program_workouts (
          *,
          program_exercises (
            id,
            workout_id,
            exercise_id,
            order_index,
            target_sets,
            target_reps_min,
            target_reps_max,
            rest_seconds
          )
        )
      `)
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // No rows returned
        return null;
      }
      console.error('Error fetching program by ID:', error);
      throw new Error(`Failed to fetch program: ${error.message}`);
    }

    const transformed = await this.transformProgramsDataWithExercises([data]);
    return transformed[0] || null;
  }

  /**
   * Get the currently active program
   * Requirements: 4.3, 4.4
   */
  async getActiveProgram(): Promise<TrainingProgram | null> {
    const { data, error } = await supabase
      .from('training_programs')
      .select(`
        *,
        program_workouts (
          *,
          program_exercises (
            id,
            workout_id,
            exercise_id,
            order_index,
            target_sets,
            target_reps_min,
            target_reps_max,
            rest_seconds
          )
        )
      `)
      .eq('is_active', true)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // No active program found
        return null;
      }
      console.error('Error fetching active program:', error);
      throw new Error(`Failed to fetch active program: ${error.message}`);
    }

    const transformed = await this.transformProgramsDataWithExercises([data]);
    return transformed[0] || null;
  }

  /**
   * Update program name
   * Requirements: 4.1
   */
  async updateProgramName(id: string, name: string): Promise<TrainingProgram> {
    if (!name.trim()) {
      throw new Error('Program name is required');
    }

    const { error } = await supabase
      .from('training_programs')
      .update({ 
        name: name.trim(),
        updated_at: new Date().toISOString()
      })
      .eq('id', id);

    if (error) {
      console.error('Error updating program name:', error);
      throw new Error(`Failed to update program name: ${error.message}`);
    }

    // Get full program data with workouts
    const fullProgram = await this.getProgramById(id);
    if (!fullProgram) {
      throw new Error('Program not found after update');
    }

    return fullProgram;
  }

  /**
   * Set a program as active (deactivates all others)
   * Requirements: 4.3, 4.4
   */
  async setActiveProgram(id: string): Promise<TrainingProgram> {
    // First, deactivate all programs
    await this.deactivateAllPrograms();

    // Then activate the specified program
    const { error } = await supabase
      .from('training_programs')
      .update({ 
        is_active: true,
        updated_at: new Date().toISOString()
      })
      .eq('id', id);

    if (error) {
      console.error('Error setting active program:', error);
      throw new Error(`Failed to set active program: ${error.message}`);
    }

    // Get full program data with workouts
    const fullProgram = await this.getProgramById(id);
    if (!fullProgram) {
      throw new Error('Program not found after activation');
    }

    return fullProgram;
  }

  /**
   * Delete a program (preserves associated workout history)
   * Requirements: 4.2, 4.5
   */
  /**
   * Delete a training program with history preservation
   * Requirements: 4.2, 4.5
   */
  async deleteProgram(id: string, options?: { 
    preserveHistory?: boolean;
    confirmationCallback?: () => Promise<boolean>;
  }): Promise<void> {
    const preserveHistory = options?.preserveHistory ?? true;
    
    try {
      // Check if program has associated workout history
      const { data: workoutHistory, error: historyError } = await supabase
        .from('workouts')
        .select('id, completed_at')
        .eq('program_id', id)
        .not('completed_at', 'is', null);

      if (historyError) {
        console.error('Error checking workout history:', historyError);
        throw new Error('Ошибка при проверке истории тренировок');
      }

      // If there's workout history and we want to preserve it, ask for confirmation
      if (workoutHistory && workoutHistory.length > 0) {
        if (options?.confirmationCallback) {
          const confirmed = await options.confirmationCallback();
          if (!confirmed) {
            return; // User cancelled deletion
          }
        }

        if (preserveHistory) {
          // Soft delete: mark program as deleted but keep workout history
          const { error: updateError } = await supabase
            .from('training_programs')
            .update({ 
              name: `[УДАЛЕНО] ${new Date().toLocaleDateString()} - ${id.slice(0, 8)}`,
              is_active: false 
            })
            .eq('id', id);

          if (updateError) {
            console.error('Error soft-deleting program:', updateError);
            throw new Error('Ошибка при удалении программы');
          }

          // Delete program structure but preserve workout history
          await this.deleteProgramStructure(id);
          return;
        }
      }

      // Hard delete: remove everything including history
      await this.hardDeleteProgram(id);

    } catch (error) {
      console.error('Error deleting program:', error);
      throw error instanceof Error ? error : new Error('Ошибка при удалении программы');
    }
  }

  /**
   * Delete program structure (workouts and exercises) but preserve workout history
   * Private helper method
   */
  private async deleteProgramStructure(programId: string): Promise<void> {
    // First get workout IDs for this program
    const { data: workoutIds, error: workoutIdsError } = await supabase
      .from('program_workouts')
      .select('id')
      .eq('program_id', programId);

    if (workoutIdsError) {
      console.error('Error getting workout IDs:', workoutIdsError);
      throw new Error('Ошибка при получении ID тренировок');
    }

    // Delete program exercises first (due to foreign key constraints)
    if (workoutIds && workoutIds.length > 0) {
      const workoutIdsList = workoutIds.map(w => w.id);
      const { error: exercisesError } = await supabase
        .from('program_exercises')
        .delete()
        .in('workout_id', workoutIdsList);

      if (exercisesError) {
        console.error('Error deleting program exercises:', exercisesError);
        throw new Error('Ошибка при удалении упражнений программы');
      }
    }

    // Delete program workouts
    const { error: workoutsError } = await supabase
      .from('program_workouts')
      .delete()
      .eq('program_id', programId);

    if (workoutsError) {
      console.error('Error deleting program workouts:', workoutsError);
      throw new Error('Ошибка при удалении тренировок программы');
    }
  }

  /**
   * Hard delete program and all associated data including workout history
   * Private helper method - use with caution!
   */
  private async hardDeleteProgram(programId: string): Promise<void> {
    // Delete in correct order due to foreign key constraints
    
    // 1. Get workout IDs for this program
    const { data: workoutIds, error: workoutIdsError } = await supabase
      .from('workouts')
      .select('id')
      .eq('program_id', programId);

    if (workoutIdsError) {
      console.error('Error getting workout IDs for deletion:', workoutIdsError);
      throw new Error('Ошибка при получении ID тренировок для удаления');
    }

    // 2. Delete sets from workouts of this program
    if (workoutIds && workoutIds.length > 0) {
      const workoutIdsList = workoutIds.map(w => w.id);
      const { error: setsError } = await supabase
        .from('sets')
        .delete()
        .in('workout_id', workoutIdsList);

      if (setsError) {
        console.error('Error deleting workout sets:', setsError);
        throw new Error('Ошибка при удалении подходов');
      }
    }

    // 3. Delete workout sessions
    const { error: workoutSessionsError } = await supabase
      .from('workouts')
      .delete()
      .eq('program_id', programId);

    if (workoutSessionsError) {
      console.error('Error deleting workout sessions:', workoutSessionsError);
      throw new Error('Ошибка при удалении сессий тренировок');
    }

    // 4. Delete program structure
    await this.deleteProgramStructure(programId);

    // 5. Finally delete the program itself
    const { error: programError } = await supabase
      .from('training_programs')
      .delete()
      .eq('id', programId);

    if (programError) {
      console.error('Error deleting program:', programError);
      throw new Error('Ошибка при удалении программы');
    }
  }

  /**
   * Get workout history count for a program
   * Helper method to check if program has history before deletion
   */
  async getProgramWorkoutHistoryCount(programId: string): Promise<number> {
    const { count, error } = await supabase
      .from('workouts')
      .select('*', { count: 'exact', head: true })
      .eq('program_id', programId)
      .not('completed_at', 'is', null);

    if (error) {
      console.error('Error counting workout history:', error);
      return 0;
    }

    return count || 0;
  }

  /**
   * Add a workout to a program
   * Requirements: 3.2
   */
  async addWorkoutToProgram(programId: string, workoutName: string): Promise<ProgramWorkout> {
    if (!workoutName.trim()) {
      throw new Error('Workout name is required');
    }

    if (this.isDevelopmentMode()) {
      console.log('Development mode: creating mock workout');
      
      // Create mock workout
      const mockWorkout: ProgramWorkout = {
        id: `mock-workout-${Date.now()}-${Math.random()}`,
        program_id: programId,
        name: workoutName.trim(),
        order_index: 0, // In real implementation, this would be calculated
        exercises: []
      };

      return mockWorkout;
    }

    // Get the next order index
    const { data: existingWorkouts, error: countError } = await supabase
      .from('program_workouts')
      .select('order_index')
      .eq('program_id', programId)
      .order('order_index', { ascending: false })
      .limit(1);

    if (countError) {
      console.error('Error getting workout count:', countError);
      throw new Error(`Failed to get workout count: ${countError.message}`);
    }

    const nextOrderIndex = existingWorkouts && existingWorkouts.length > 0 
      ? existingWorkouts[0].order_index + 1 
      : 0;

    const { data, error } = await supabase
      .from('program_workouts')
      .insert({
        program_id: programId,
        name: workoutName.trim(),
        order_index: nextOrderIndex
      })
      .select()
      .single();

    if (error) {
      console.error('Error adding workout to program:', error);
      throw new Error(`Failed to add workout to program: ${error.message}`);
    }

    return {
      ...data,
      exercises: []
    };
  }

  /**
   * Update workout name
   * Requirements: 4.1
   */
  async updateWorkoutName(workoutId: string, name: string): Promise<ProgramWorkout> {
    if (!name.trim()) {
      throw new Error('Workout name is required');
    }

    const { error } = await supabase
      .from('program_workouts')
      .update({ name: name.trim() })
      .eq('id', workoutId);

    if (error) {
      console.error('Error updating workout name:', error);
      throw new Error(`Failed to update workout name: ${error.message}`);
    }

    // Get the updated workout with exercises
    const { data: updatedData, error: fetchError } = await supabase
      .from('program_workouts')
      .select(`
        *,
        program_exercises (
          *,
          exercise_catalog (*)
        )
      `)
      .eq('id', workoutId)
      .single();

    if (fetchError) {
      console.error('Error fetching updated workout:', fetchError);
      throw new Error(`Failed to fetch updated workout: ${fetchError.message}`);
    }

    return {
      ...updatedData,
      exercises: (updatedData.program_exercises || [])
        .sort((a: any, b: any) => a.order_index - b.order_index)
        .map((exercise: any) => this.transformProgramExercise(exercise))
    };
  }

  /**
   * Delete a workout from a program
   * Requirements: 4.2
   */
  async deleteWorkout(workoutId: string): Promise<void> {
    const { error } = await supabase
      .from('program_workouts')
      .delete()
      .eq('id', workoutId);

    if (error) {
      console.error('Error deleting workout:', error);
      throw new Error(`Failed to delete workout: ${error.message}`);
    }
  }

  /**
   * Add an exercise to a workout
   * Requirements: 3.3
   */
  async addExerciseToWorkout(
    workoutId: string, 
    exerciseId: string, 
    targetSets?: number,
    targetRepsMin?: number,
    targetRepsMax?: number,
    restSeconds?: number
  ): Promise<ProgramExercise> {
    console.log('🔍 addExerciseToWorkout called with:', {
      workoutId,
      exerciseId,
      targetSets,
      targetRepsMin,
      targetRepsMax,
      restSeconds
    });

    if (this.isDevelopmentMode()) {
      console.log('Development mode: creating mock program exercise');
      
      // Get exercise from catalog service
      const exercise = await exerciseCatalogService.getExerciseById(exerciseId);
      
      if (!exercise) {
        throw new Error('Exercise not found');
      }

      // Create mock program exercise
      const mockProgramExercise: ProgramExercise = {
        id: `mock-program-exercise-${Date.now()}-${Math.random()}`,
        workout_id: workoutId,
        order_index: 0, // In real implementation, this would be calculated
        target_sets: targetSets,
        target_reps_min: targetRepsMin,
        target_reps_max: targetRepsMax,
        rest_seconds: restSeconds,
        exercise: exercise
      };

      return mockProgramExercise;
    }

    // Get the next order index
    const { data: existingExercises, error: countError } = await supabase
      .from('program_exercises')
      .select('order_index')
      .eq('workout_id', workoutId)
      .order('order_index', { ascending: false })
      .limit(1);

    if (countError) {
      console.error('Error getting exercise count:', countError);
      throw new Error(`Failed to get exercise count: ${countError.message}`);
    }

    const nextOrderIndex = existingExercises && existingExercises.length > 0 
      ? existingExercises[0].order_index + 1 
      : 0;

    console.log('📝 Inserting exercise into program_exercises:', {
      workout_id: workoutId,
      exercise_id: exerciseId,
      order_index: nextOrderIndex
    });

    // Insert the program exercise WITHOUT trying to join exercise_catalog
    const { data, error } = await supabase
      .from('program_exercises')
      .insert({
        workout_id: workoutId,
        exercise_id: exerciseId,
        order_index: nextOrderIndex,
        target_sets: targetSets,
        target_reps_min: targetRepsMin,
        target_reps_max: targetRepsMax,
        rest_seconds: restSeconds
      })
      .select()
      .single();

    if (error) {
      console.error('❌ Error adding exercise to workout:', error);
      console.error('📋 Error details:', {
        code: error.code,
        message: error.message,
        details: error.details,
        hint: error.hint
      });
      
      // Show detailed error
      alert(`Ошибка добавления упражнения:\nКод: ${error.code}\nСообщение: ${error.message}\nExercise ID: ${exerciseId}`);
      
      throw new Error(`Failed to add exercise to workout: ${error.message}`);
    }

    console.log('✅ Exercise added to program_exercises, now fetching exercise details...');

    // Separately fetch the exercise details from exercise_catalog
    const { data: exerciseData, error: exerciseError } = await supabase
      .from('exercise_catalog')
      .select('*')
      .eq('id', exerciseId)
      .single();

    if (exerciseError || !exerciseData) {
      console.error('❌ Error fetching exercise details:', exerciseError);
      throw new Error(`Failed to fetch exercise details: ${exerciseError?.message}`);
    }

    console.log('✅ Exercise details fetched successfully');

    // Combine the data manually
    return {
      id: data.id,
      workout_id: data.workout_id,
      order_index: data.order_index,
      target_sets: data.target_sets,
      target_reps_min: data.target_reps_min,
      target_reps_max: data.target_reps_max,
      rest_seconds: data.rest_seconds,
      exercise: {
        id: exerciseData.id,
        name: exerciseData.name_en,
        name_ru: exerciseData.name_ru,
        muscle_group: exerciseData.primary_muscles?.[0] || '',
        equipment: exerciseData.equipment?.[0] || '',
        instructions: exerciseData.description || ''
      }
    };
  }

  /**
   * Update exercise order in a workout
   * Requirements: 3.4
   */
  async updateExerciseOrder(workoutId: string, exerciseOrders: Array<{ id: string; order_index: number }>): Promise<void> {
    // Update all exercise orders in a transaction-like manner
    const updates = exerciseOrders.map(({ id, order_index }) => 
      supabase
        .from('program_exercises')
        .update({ order_index })
        .eq('id', id)
        .eq('workout_id', workoutId) // Additional safety check
    );

    const results = await Promise.all(updates);
    
    // Check for any errors
    const errors = results.filter((result: any) => result.error);
    if (errors.length > 0) {
      console.error('Error updating exercise order:', errors);
      throw new Error(`Failed to update exercise order: ${errors[0].error?.message}`);
    }
  }

  /**
   * Remove an exercise from a workout
   * Requirements: 4.2
   */
  async removeExerciseFromWorkout(exerciseId: string): Promise<void> {
    const { error } = await supabase
      .from('program_exercises')
      .delete()
      .eq('id', exerciseId);

    if (error) {
      console.error('Error removing exercise from workout:', error);
      throw new Error(`Failed to remove exercise from workout: ${error.message}`);
    }
  }

  /**
   * Update exercise targets (sets, reps)
   * Requirements: 3.3
   */
  async updateExerciseTargets(
    exerciseId: string,
    targetSets?: number,
    targetRepsMin?: number,
    targetRepsMax?: number,
    restSeconds?: number
  ): Promise<ProgramExercise> {
    const { data, error } = await supabase
      .from('program_exercises')
      .update({
        target_sets: targetSets,
        target_reps_min: targetRepsMin,
        target_reps_max: targetRepsMax,
        rest_seconds: restSeconds
      })
      .eq('id', exerciseId)
      .select(`
        *,
        exercise_catalog (*)
      `)
      .single();

    if (error) {
      console.error('Error updating exercise targets:', error);
      throw new Error(`Failed to update exercise targets: ${error.message}`);
    }

    return this.transformProgramExercise(data);
  }

  /**
   * Get programs summary (for dashboard/overview)
   */
  async getProgramsSummary(): Promise<Array<{
    id: string;
    name: string;
    is_active: boolean;
    workout_count: number;
    total_exercises: number;
    created_at: string;
  }>> {
    const { data, error } = await supabase
      .from('training_programs')
      .select(`
        id,
        name,
        is_active,
        created_at,
        program_workouts (
          id,
          program_exercises (id)
        )
      `)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error fetching programs summary:', error);
      throw new Error(`Failed to fetch programs summary: ${error.message}`);
    }

    return (data || []).map((program: any) => ({
      id: program.id,
      name: program.name,
      is_active: program.is_active,
      workout_count: program.program_workouts?.length || 0,
      total_exercises: program.program_workouts?.reduce((total: number, workout: any) => 
        total + (workout.program_exercises?.length || 0), 0) || 0,
      created_at: program.created_at
    }));
  }

  /**
   * Private helper: Deactivate all programs for the current user
   */
  private async deactivateAllPrograms(): Promise<void> {
    const { error } = await supabase
      .from('training_programs')
      .update({ 
        is_active: false,
        updated_at: new Date().toISOString()
      })
      .eq('is_active', true);

    if (error) {
      console.error('Error deactivating programs:', error);
      throw new Error(`Failed to deactivate programs: ${error.message}`);
    }
  }

  /**
   * Private helper: Transform database program data to application format
   */
  private transformProgramsData(data: any[]): TrainingProgram[] {
    return data.map(program => ({
      id: program.id,
      user_id: program.user_id,
      name: program.name,
      is_active: program.is_active,
      created_at: program.created_at,
      updated_at: program.updated_at,
      workouts: (program.program_workouts || [])
        .sort((a: any, b: any) => a.order_index - b.order_index)
        .map((workout: any) => ({
          id: workout.id,
          program_id: workout.program_id,
          name: workout.name,
          order_index: workout.order_index,
          exercises: (workout.program_exercises || [])
            .sort((a: any, b: any) => a.order_index - b.order_index)
            .map((exercise: any) => this.transformProgramExercise(exercise))
        }))
    }));
  }

  /**
   * Private helper: Transform program data and load exercise details separately
   */
  private async transformProgramsDataWithExercises(data: any[]): Promise<TrainingProgram[]> {
    // Collect all unique exercise IDs
    const exerciseIds = new Set<string>();
    data.forEach(program => {
      (program.program_workouts || []).forEach((workout: any) => {
        (workout.program_exercises || []).forEach((exercise: any) => {
          if (exercise.exercise_id) {
            exerciseIds.add(exercise.exercise_id);
          }
        });
      });
    });

    // Load all exercises in one query
    const exerciseMap = new Map<string, any>();
    if (exerciseIds.size > 0) {
      const { data: exercises, error } = await supabase
        .from('exercise_catalog')
        .select('*')
        .in('id', Array.from(exerciseIds));

      if (!error && exercises) {
        exercises.forEach(ex => exerciseMap.set(ex.id, ex));
      }
    }

    // Transform data with exercise details
    return data.map(program => ({
      id: program.id,
      user_id: program.user_id,
      name: program.name,
      is_active: program.is_active,
      created_at: program.created_at,
      updated_at: program.updated_at,
      workouts: (program.program_workouts || [])
        .sort((a: any, b: any) => a.order_index - b.order_index)
        .map((workout: any) => ({
          id: workout.id,
          program_id: workout.program_id,
          name: workout.name,
          order_index: workout.order_index,
          exercises: (workout.program_exercises || [])
            .sort((a: any, b: any) => a.order_index - b.order_index)
            .map((exercise: any) => {
              const exerciseData = exerciseMap.get(exercise.exercise_id);
              return {
                id: exercise.id,
                workout_id: exercise.workout_id,
                order_index: exercise.order_index,
                target_sets: exercise.target_sets,
                target_reps_min: exercise.target_reps_min,
                target_reps_max: exercise.target_reps_max,
                rest_seconds: exercise.rest_seconds,
                exercise: exerciseData ? {
                  id: exerciseData.id,
                  name_ru: exerciseData.name_ru || '',
                  name_en: exerciseData.name_en || exerciseData.name || '',
                  description: exerciseData.description || '',
                  primary_muscles: exerciseData.primary_muscles || [],
                  secondary_muscles: exerciseData.secondary_muscles || [],
                  equipment: exerciseData.equipment || [],
                  tags: exerciseData.tags || [],
                  exercise_type: exerciseData.exercise_type,
                  difficulty_level: exerciseData.difficulty_level,
                  muscle_group: exerciseData.primary_muscles?.[0] || '',
                  instructions: exerciseData.description || ''
                } : {
                  id: exercise.exercise_id,
                  name_ru: 'Неизвестное упражнение',
                  name_en: 'Unknown',
                  description: '',
                  primary_muscles: [],
                  secondary_muscles: [],
                  equipment: [],
                  tags: [],
                  muscle_group: '',
                  instructions: ''
                }
              };
            })
        }))
    }));
  }

  /**
   * Private helper: Transform program exercise data
   */
  private transformProgramExercise(data: any): ProgramExercise {
    return {
      id: data.id,
      workout_id: data.workout_id,
      order_index: data.order_index,
      target_sets: data.target_sets,
      target_reps_min: data.target_reps_min,
      target_reps_max: data.target_reps_max,
      rest_seconds: data.rest_seconds,
      exercise: {
        id: data.exercise_catalog.id,
        name_ru: data.exercise_catalog.name_ru || '',
        name_en: data.exercise_catalog.name_en || data.exercise_catalog.name || '',
        description: data.exercise_catalog.description || '',
        primary_muscles: data.exercise_catalog.primary_muscles || [],
        secondary_muscles: data.exercise_catalog.secondary_muscles || [],
        equipment: data.exercise_catalog.equipment || [],
        tags: data.exercise_catalog.tags || [],
        exercise_type: data.exercise_catalog.exercise_type,
        difficulty_level: data.exercise_catalog.difficulty_level,
        muscle_group: data.exercise_catalog.primary_muscles?.[0] || '',
        instructions: data.exercise_catalog.description || ''
      }
    };
  }
}

// Export a singleton instance
export const trainingProgramService = new TrainingProgramService();